/**
 * Lightweight download helpers for presigned/remote URLs.
 *
 * The OMS backend ALWAYS pre-signs carrier image and bulk-download URLs
 * with a `response-content-disposition=attachment; filename="..."` query
 * parameter (see `S3Client#getPresignedUrlForDownload`). When S3 honors that
 * override, its response carries `Content-Disposition: attachment`, which
 * causes the browser to trigger a direct download rather than navigating
 * to the URL — even cross-origin, and even WITHOUT S3 bucket CORS.
 *
 * Strategy:
 *   `<a href>` direct click. Anchor navigation is exempt from CORS, and
 *   because the response has Content-Disposition: attachment the browser
 *   triggers a download instead of navigation. The user's tab stays put,
 *   no preflight is sent, no CORS errors appear in the Network tab.
 *
 * We intentionally do NOT use `fetch()` or `XMLHttpRequest` because S3
 * bucket CORS is not configured for our origins, so those would fail
 * loudly in the Network tab even though the anchor strategy succeeds.
 */

export const DOWNLOAD_MODES = {
  ANCHOR_ATTACHMENT: "anchor-attachment",
};

/**
 * Thrown when the anchor-click download cannot be triggered. In practice
 * this only fires in non-browser environments (SSR, no `document`).
 */
export class DownloadBlockedError extends Error {
  constructor(message) {
    super(message);
    this.name = "DownloadBlockedError";
  }
}

/**
 * Open a presigned URL in a new tab — useful only as an explicit user
 * action, never as a download fallback.
 */
export function openInNewTab(url) {
  if (!url) return;
  if (typeof window === "undefined") return;
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Sanitize a string so it's safe to use as a filename segment.
 */
export function safeFilenameSegment(input, maxLen = 200) {
  if (!input) return "";
  return String(input)
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLen);
}

/**
 * Force-download a remote file via an anchor click. Relies on the backend
 * pre-signing the URL with `Content-Disposition: attachment`, so the
 * browser downloads instead of navigating. The `filename` argument is
 * preserved for API compatibility but the actual file name comes from the
 * `Content-Disposition` header on the S3 response (cross-origin browsers
 * ignore the local `download` attribute).
 *
 * @param {string} url
 * @param {string} filename
 * @returns {Promise<{ mode: string }>}
 */
// eslint-disable-next-line no-unused-vars
export async function downloadRemoteFile(url, filename) {
  if (!url) throw new Error("Download URL is required");
  if (typeof window === "undefined") throw new Error("No window available");
  if (typeof document === "undefined") {
    throw new DownloadBlockedError("No document available for anchor download");
  }

  // `filename` is informational only when the backend sets Content-Disposition.
  // We sanitize defensively in case we ever fall back to same-origin downloads.
  void safeFilenameSegment(filename, 200);

  triggerDirectAnchorDownload(url);
  return { mode: DOWNLOAD_MODES.ANCHOR_ATTACHMENT };
}

/* ────────────────────────────────────────────────────────────────────────── */
/*                                  Helpers                                   */
/* ────────────────────────────────────────────────────────────────────────── */

function triggerDirectAnchorDownload(url) {
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener noreferrer";
  // Intentionally omit `a.target = '_blank'` — when the response has
  // Content-Disposition: attachment, the browser triggers a download and
  // does NOT navigate the current tab. Opening in _blank would briefly
  // flash a blank tab on some browsers.
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
